import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Download, Library, Settings as SettingsIcon, Chrome, LogOut, Sun, Moon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";

const linkBase =
  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-150";
const linkActive = "bg-secondary text-foreground";

export default function DashboardLayout() {
  const { logout, user } = useAuth();
  const { t } = useI18n();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const items = [
    { to: "/", key: "dashboard", icon: Library, end: true },
    { to: "/extension", key: "extension", icon: Chrome },
    { to: "/settings", key: "settings", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen grid grid-cols-[240px_1fr] bg-background">
      <aside className="border-r border-border h-screen sticky top-0 flex flex-col">
        <div className="px-5 py-5 flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
            <Download strokeWidth={1.75} className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-tight">Ads Library</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Downloader</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.key}
              to={it.to}
              end={it.end}
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
              data-testid={`nav-${it.key}`}
            >
              <it.icon strokeWidth={1.5} className="h-4 w-4" />
              <span>{t(`nav.${it.key}`)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border flex flex-col gap-2">
          <div className="px-2 py-1 text-xs text-muted-foreground truncate">
            {user?.email}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun strokeWidth={1.5} className="h-4 w-4" /> : <Moon strokeWidth={1.5} className="h-4 w-4" />}
            </Button>
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto"
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="btn-logout"
              aria-label="Sign out"
            >
              <LogOut strokeWidth={1.5} className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="min-w-0">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
